// Stock (spec/13 §6.4; ADR-0040; F-FNB-07 … F-FNB-13): one place for what a venue keeps on its shelves — food and drink
// ingredients, retail items sold off the shelf and the venue's own supplies. Five tabs, each its own URL
// (`…/stock/{tab}`):
//
//   Items       the stock list: on hand in its natural unit, par, its state in words, value; search (a scan opens the
//               item), filters and sort; a row opens the item — its history, its details and packs
//   Counts      full and spot counts, the open one first; a count is counted on its own page (`CountPage.tsx`)
//   Deliveries  what was received, from whom, at what cost
//   Suppliers   who the venue buys from
//   Reports     stock value, usage, count variance and margins (`inventory:reports`)
//
// The header's actions — Receive delivery, Count stock, Record usage — open centred dialogs over whichever tab is
// showing, each offered only with its permission (and held to supplies for a member who handles supplies alone).
//
// **Off** (`inventory.enabled`, the default): the section is listed only to a member who can turn it on, and the
// page is a calm card saying what tracking does and that the first step after turning it on is a full count. **On,
// never counted**: a notice asks for that full count first, because until then every item reads zero.

import type { StockItem, StockItemKind, StockItemPack } from '@clavian/api-client';
import { isReportPreset, type ReportPreset } from '@clavian/domain/reports';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, EmptyState, Tabs, type TabItem } from '@clavian/ui-web';
import { Link, Navigate, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { CardsSkeleton, Forbidden, Notice, Page } from '../../ui';
import { useStockAccess } from './access';
import { CopyFromVenueDialog } from './CopyFromVenueDialog';
import { CountsTab, StartCountDialog } from './Counts';
import { DeliveriesTab, ReceiveDialog } from './Receive';
import { ItemDetailDialog } from './ItemDetail';
import { ItemEditorDialog } from './ItemEditor';
import { useStockStatus } from './queries';
import { StockItemsTab } from './StockItemsTab';
import { STOCK_REPORTS, StockReportsTab, type StockReportName } from './StockReports';
import { SuppliersTab } from './Suppliers';
import { UsageDialog, type UsageMode } from './UsageDialog';

import './stock.css';

export const STOCK_TABS = ['items', 'counts', 'deliveries', 'suppliers', 'reports'] as const;
export type StockTab = (typeof STOCK_TABS)[number];

/** The Items tab's filter in the URL, so "What needs you" can open the list filtered. */
export const STOCK_LEVEL_FILTERS = ['attention', 'low', 'out', 'negative'] as const;
export type StockLevelFilter = (typeof STOCK_LEVEL_FILTERS)[number];

export interface StockSearch {
  readonly level?: StockLevelFilter;
  readonly kind?: StockItemKind;
  /** An item to open: "What needs you" and a scan link straight to it. */
  readonly item?: string;
  /** A delivery to open on the Deliveries tab. */
  readonly receipt?: string;
  /** The Reports tab's report and range, so a report is a link to share. */
  readonly report?: StockReportName;
  readonly range?: ReportPreset;
}

/** The route's `validateSearch`: keeps what is well formed, drops the rest. */
export function readStockSearch(raw: Record<string, unknown>): StockSearch {
  const text = (key: string) => (typeof raw[key] === 'string' ? (raw[key] as string) : undefined);
  const level = text('level');
  const kind = text('kind');
  const item = text('item');
  const receipt = text('receipt');
  const report = text('report');
  const range = text('range');
  return {
    ...(level !== undefined && (STOCK_LEVEL_FILTERS as readonly string[]).includes(level) ? { level: level as StockLevelFilter } : {}),
    ...(kind === 'ingredient' || kind === 'retail' || kind === 'supply' ? { kind } : {}),
    ...(item !== undefined && item.startsWith('ski_') ? { item } : {}),
    ...(receipt !== undefined && receipt.startsWith('skr_') ? { receipt } : {}),
    ...(report !== undefined && (STOCK_REPORTS as readonly string[]).includes(report) ? { report: report as StockReportName } : {}),
    ...(range !== undefined && isReportPreset(range) ? { range } : {}),
  };
}

/** A dialog opened from the page: what it is, and the item (and scanned pack) it starts on. */
export type StockDialog =
  | { readonly kind: 'receive'; readonly item?: StockItem; readonly pack?: StockItemPack | null }
  | { readonly kind: 'usage'; readonly mode?: UsageMode; readonly item?: StockItem }
  | { readonly kind: 'count'; readonly full?: boolean; readonly item?: StockItem }
  | { readonly kind: 'newItem' }
  | { readonly kind: 'editItem'; readonly item: StockItem }
  | { readonly kind: 'copy' };

export function StockPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { readonly tab?: string };
  const search = readStockSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const access = useStockAccess(venue.id);
  const status = useStockStatus(venue.organizationId, venue.id, access.read || !access.ready);
  const [dialog, setDialog] = useState<StockDialog | null>(null);

  const tabs: readonly StockTab[] = STOCK_TABS.filter((tab) => tab !== 'reports' || access.reports);
  if (params.tab === undefined || !(tabs as readonly string[]).includes(params.tab)) {
    // An unknown tab, or Reports for a role without the costs, opens the list instead.
    if (!access.ready && params.tab === 'reports') return <Loading />;
    return <Navigate to="/v/$venueSlug/stock/$tab" params={{ venueSlug: venue.slug, tab: 'items' }} replace />;
  }
  const tab = params.tab as StockTab;
  const setSearch = (patch: Partial<StockSearch>) =>
    void navigate({
      to: '/v/$venueSlug/stock/$tab',
      params: { venueSlug: venue.slug, tab },
      search: (current: Record<string, unknown>) => Object.fromEntries(Object.entries({ ...readStockSearch(current), ...patch }).filter(([, value]) => value !== undefined)),
      replace: true,
    });
  const open = (next: StockTab) => void navigate({ to: '/v/$venueSlug/stock/$tab', params: { venueSlug: venue.slug, tab: next } });

  let body: ReactNode;
  if (access.ready && !access.read) body = <Forbidden message={t('stock.notPermitted')} />;
  else if (status.data === undefined) {
    if (status.isError && problemCode(status.error) === PROBLEM.forbidden) body = <Forbidden message={t('stock.notPermitted')} />;
    else body = status.isError ? <LoadFailure error={status.error} message={t('stock.unavailable')} onRetry={() => void status.refetch()} /> : <Loading />;
  } else if (!status.data.enabled) {
    body = <TurnOnCard canTurnOn={access.turnOn} venueSlug={venue.slug} venueName={venue.name} />;
  } else {
    const content: Record<StockTab, ReactNode> = {
      items: <StockItemsTab status={status.data} access={access} search={search} setSearch={setSearch} openDialog={setDialog} />,
      counts: <CountsTab access={access} onStart={() => setDialog({ kind: 'count' })} />,
      deliveries: <DeliveriesTab access={access} receiptId={search.receipt ?? null} onOpenReceipt={(receipt) => setSearch({ receipt: receipt ?? undefined })} onReceive={() => setDialog({ kind: 'receive' })} />,
      suppliers: <SuppliersTab access={access} />,
      reports: <StockReportsTab search={search} setSearch={setSearch} />,
    };
    const items: TabItem[] = tabs.map((value) => ({ value, label: t(`stock.tabs.${value}`), content: content[value] }));
    body = (
      <>
        {status.data.lastFullCountAt === null ? (
          <Notice
            tone="info"
            title={t('stock.firstCount.title')}
            action={
              access.count && !access.countSuppliesOnly ? (
                <Button variant="primary" size="sm" onClick={() => setDialog({ kind: 'count', full: true })}>
                  {t('stock.firstCount.action')}
                </Button>
              ) : undefined
            }
          >
            <p>{t('stock.firstCount.body')}</p>
          </Notice>
        ) : null}
        <Tabs label={t('stock.tabs.label')} items={items} value={tab} onValueChange={(value) => open(value as StockTab)} className="console-page-tabs" />
      </>
    );
  }

  const on = status.data?.enabled === true;
  return (
    <Page
      title={t('stock.title')}
      description={on ? t('stock.intro') : t('stock.introOff')}
      actions={on ? <HeaderActions access={access} onOpen={setDialog} /> : undefined}
    >
      {body}
      {on ? (
        <>
          <ReceiveDialog dialog={dialog?.kind === 'receive' ? dialog : null} access={access} onClose={() => setDialog(null)} />
          <UsageDialog dialog={dialog?.kind === 'usage' ? dialog : null} access={access} onClose={() => setDialog(null)} />
          <StartCountDialog dialog={dialog?.kind === 'count' ? dialog : null} access={access} onClose={() => setDialog(null)} />
          <ItemEditorDialog dialog={dialog?.kind === 'newItem' || dialog?.kind === 'editItem' ? dialog : null} access={access} onClose={() => setDialog(null)} />
          <CopyFromVenueDialog open={dialog?.kind === 'copy'} onClose={() => setDialog(null)} />
          <ItemDetailDialog
            stockItemId={search.item ?? null}
            access={access}
            onClose={() => setSearch({ item: undefined })}
            onAction={(next) => {
              setSearch({ item: undefined });
              setDialog(next);
            }}
          />
        </>
      ) : null}
    </Page>
  );
}

/** The page's one primary action — Receive delivery — and the two others beside it, each with its permission. */
function HeaderActions({ access, onOpen }: { readonly access: ReturnType<typeof useStockAccess>; readonly onOpen: (dialog: StockDialog) => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <>
      {access.usage || access.adjust ? (
        <Button variant="secondary" icon={<Icon name="minus" />} onClick={() => onOpen({ kind: 'usage' })}>
          {t('stock.actions.usage')}
        </Button>
      ) : null}
      {access.count ? (
        <Button variant="secondary" icon={<Icon name="list" />} onClick={() => onOpen({ kind: 'count' })}>
          {t('stock.actions.count')}
        </Button>
      ) : null}
      {access.receive ? (
        <Button variant="primary" icon={<Icon name="download" />} onClick={() => onOpen({ kind: 'receive' })}>
          {t('stock.actions.receive')}
        </Button>
      ) : null}
    </>
  );
}

/**
 * Stock is off here (the default): what tracking does, that turning it on starts every item at zero and the first step
 * is a full count, and — for a member who may — the way to the setting. Calm: a card, not an alarm.
 */
function TurnOnCard({ canTurnOn, venueSlug, venueName }: { readonly canTurnOn: boolean; readonly venueSlug: string; readonly venueName: string }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <section className="console-stock-off" aria-labelledby="console-stock-off-title">
      <span className="console-stock-off__icon" aria-hidden="true">
        <NavIcon name="stock" />
      </span>
      <div className="console-stock-off__body">
        <h2 id="console-stock-off-title" className="console-stock-off__title">
          {t('stock.off.title', { venue: venueName })}
        </h2>
        <p className="console-stock-off__lead">{t('stock.off.lead')}</p>
        <ul className="console-stock-off__points">
          {(['recipes', 'alerts', 'deliveries', 'margins'] as const).map((point) => (
            <li key={point}>
              <span className="console-stock-off__tick" aria-hidden="true">
                <Icon name="check" />
              </span>
              {t(`stock.off.points.${point}`)}
            </li>
          ))}
        </ul>
        <p className="console-stock-off__first">{t('stock.off.firstStep')}</p>
        {canTurnOn ? (
          <div className="console-stock-off__actions">
            <Button variant="primary" asChild>
              <Link to="/v/$venueSlug/settings/$group" params={{ venueSlug, group: 'foodAndOrders' }} hash="inventory.enabled">
                {t('stock.off.turnOn')}
              </Link>
            </Button>
          </div>
        ) : (
          <p className="console-subtle">{t('stock.off.askManager')}</p>
        )}
      </div>
    </section>
  );
}

function Loading(): ReactNode {
  const { t } = useTranslation('console');
  return <CardsSkeleton count={4} height="5rem" label={t('stock.loading')} />;
}

/** An empty list with what to do next, in the list's place. */
export function StockEmpty({ title, description, action }: { readonly title: string; readonly description: string; readonly action?: ReactNode }): ReactNode {
  return <EmptyState icon={<NavIcon name="stock" />} title={title} description={description} action={action} />;
}

/** "26 Sep, 7:10 pm" in the venue's time. */
export function useWhen(): (instant: string) => string {
  const format = useVenueFormatter();
  return (instant) => format.custom(instant, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}
